import { useState, useEffect, useCallback } from 'react';
import {
  OFFLINE_FILE_KEYS,
  OFFLINE_STATUS,
  getPatientOfflineStatusRecord,
  downloadPatientAssets,
  isOfflineDownloadActive,
  subscribeToPatientOfflineStatus,
  writePatientStatus,
  deletePatientOfflineData,
  resolveDemoAssetUrls,
} from '../../services/offlineService';
import { createOfflinePatientMetadata, resetOfflineDownloadState } from '../../shared/utils/offlineUtils';

/**
 * @param {string} patientId
 * @param {string} [displayName]
 * @param {number} [createdTime]
 */
export function usePatientOfflineStatus(patientId, displayName, createdTime) {
  const [offlineStatus, setOfflineStatus] = useState(OFFLINE_STATUS.IDLE);
  const [downloadPercent, setDownloadPercent] = useState(0);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setOfflineStatus(OFFLINE_STATUS.IDLE);
      setDownloadPercent(0);
      setDownloadError(false);
      return;
    }

    let isMounted = true;

    getPatientOfflineStatusRecord(patientId)
      .then(async (record) => {
        if (!isMounted) return;

        if (record.status === OFFLINE_STATUS.DOWNLOADING && !isOfflineDownloadActive(patientId)) {
          try {
            await deletePatientOfflineData(patientId);
          } catch (cleanupErr) {
            console.warn('Failed to cleanup stale offline download state:', patientId, cleanupErr);
          }

          if (!isMounted) return;
          setOfflineStatus(OFFLINE_STATUS.IDLE);
          setDownloadPercent(0);
          setDownloadError(false);
          return;
        }

        setOfflineStatus(record.status);
        setDownloadPercent(record.status === OFFLINE_STATUS.READY ? 100 : record.downloadPercent ?? 0);
      })
      .catch((err) => {
        console.error('Failed to read offline patient status:', patientId, err);
        if (isMounted) {
          setOfflineStatus(OFFLINE_STATUS.IDLE);
          setDownloadPercent(0);
          setDownloadError(false);
        }
      });

    const unsubscribe = subscribeToPatientOfflineStatus(patientId, (status) => {
      if (isMounted) setOfflineStatus(status);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [patientId]);

  useEffect(() => {
    if (!patientId || offlineStatus !== OFFLINE_STATUS.DOWNLOADING) {
      return undefined;
    }

    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      if (cancelled) return;
      try {
        const record = await getPatientOfflineStatusRecord(patientId);
        if (cancelled) return;
        if (record.status === OFFLINE_STATUS.READY) {
          setOfflineStatus(OFFLINE_STATUS.READY);
          setDownloadPercent(100);
          return;
        }
        if (record.status === OFFLINE_STATUS.DOWNLOADING) {
          setDownloadPercent(record.downloadPercent ?? 0);
        }
      } catch (err) {
        console.warn('Failed to refresh offline download progress:', err);
      }
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [patientId, offlineStatus]);

  const startDownload = useCallback(async () => {
    if (!patientId) return;
    setDownloadError(false);
    setOfflineStatus(OFFLINE_STATUS.DOWNLOADING);
    try {
      await writePatientStatus(patientId, OFFLINE_STATUS.DOWNLOADING, 0);
      const urlMap = await resolveDemoAssetUrls(patientId);
      await downloadPatientAssets(
        patientId,
        urlMap,
        ({ percent }) => {
          setDownloadPercent(percent);
        },
        createOfflinePatientMetadata(displayName, createdTime),
      );
      setDownloadPercent(100);
      setOfflineStatus(OFFLINE_STATUS.READY);
    } catch (err) {
      console.error('Failed to download offline assets for patient:', patientId, err);
      resetOfflineDownloadState(setOfflineStatus, setDownloadPercent, setDownloadError);
      try {
        await writePatientStatus(patientId, OFFLINE_STATUS.IDLE);
      } catch (_) {}
    }
  }, [patientId, displayName, createdTime]);

  const deleteOfflineData = useCallback(async () => {
    if (!patientId) return;
    resetOfflineDownloadState(setOfflineStatus, setDownloadPercent, setDownloadError);
    try {
      await deletePatientOfflineData(patientId);
    } catch (err) {
      console.error('Failed to delete offline data for patient:', patientId, err);
    }
  }, [patientId]);

  return { offlineStatus, downloadPercent, downloadError, startDownload, deleteOfflineData };
}
